import React, { useCallback, useEffect, useState } from "react";
import { Card, Select, Loading, ErrorState } from "./ui";
import { listDepartments, saveDepartment, deleteDepartment, createUser } from "../api";
import { fmtDate } from "../lib/format";

/**
 * Which LN:nn department in the finance exports belongs to which manager, and
 * from when.
 *
 * An office number outlives the person running it, so this is dated: LN:20 can
 * be TM until 1 Jan 2025 and somebody else after, and the history either side
 * keeps reporting under whoever owned it at the time. One row per changeover;
 * an entry with no date means "always", which is how every existing mapping
 * was carried over.
 *
 * An import fails outright on a department that is not listed here, so this is
 * the one place to fix that.
 */
const ALWAYS = "1900-01-01";
const NEW_MANAGER = "__new__";
export default function DepartmentMap() {
  const [mappings, setMappings] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState("");

  const [newCode, setNewCode] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newUser, setNewUser] = useState("");
  const [newFrom, setNewFrom] = useState("");

  // Creating a manager inline. A department cannot be mapped to a login that
  // does not exist, and bouncing to Users and back to add one is friction in
  // the middle of the job.
  const [addingUser, setAddingUser] = useState(false);
  const [mgrName, setMgrName] = useState("");
  const [mgrPass, setMgrPass] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listDepartments();
      setMappings(data.mappings);
      setUsers(data.users);
      setNewUser((u) => u || data.users.find((x) => x !== "fd") || "");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remap = async (row, username) => {
    setNotice("");
    try {
      await saveDepartment(row.code, username, row.label, row.effective_from, row.note);
      await load();
    } catch (err) {
      setNotice(err.message);
    }
  };

  const createManager = async (e) => {
    e.preventDefault();
    setNotice("");
    setSaving(true);
    try {
      await createUser(mgrName.trim().toLowerCase(), mgrPass);
      const name = mgrName.trim().toLowerCase();
      await load();
      setNewUser(name);            // pre-select the manager just created
      setMgrName(""); setMgrPass(""); setAddingUser(false);
      setNotice(`Manager ${name.toUpperCase()} created and selected.`);
    } catch (err) {
      setNotice(err.message);
    } finally {
      setSaving(false);
    }
  };

  const add = async () => {
    if (!newCode || !newUser) return;
    setSaving(true);
    setNotice("");
    try {
      await saveDepartment(newCode, newUser, newLabel, newFrom || ALWAYS);
      setNewCode(""); setNewLabel(""); setNewFrom("");
      await load();
    } catch (err) {
      setNotice(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    const last = Number(row.versions) <= 1;
    const msg = last
      ? `Remove the mapping for ${row.code}?\n\nIt is the only one, so any import containing this department will fail until it is mapped again.`
      : `Remove the ${row.effective_from === ALWAYS ? "original" : row.effective_from} entry for ${row.code}?\n\nThe other entries for this code stay, so it will fall back to whichever applies.`;
    if (!window.confirm(msg)) return;
    setNotice("");
    try {
      await deleteDepartment(row.id);
      await load();
    } catch (err) {
      setNotice(err.message);
    }
  };

  if (loading) return <Loading label="Loading departments…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const options = users.map((u) => ({ value: u, label: u.toUpperCase() }));

  return (
    <div className="stack">
      <Card
        title="Departments"
        note="LN:nn in the expense dumps → the manager it belongs to"
        flush
      >
        {notice && <div className="notice notice--warn">{notice}</div>}

        <div className="dept-add">
          <div className="field">
            <span className="field-label">Department</span>
            <input
              className="input"
              placeholder="26 or LN:26"
              value={newCode}
              style={{ width: 110 }}
              onChange={(e) => setNewCode(e.target.value)}
            />
          </div>
          <div className="field" style={{ flex: 1, minWidth: 160 }}>
            <span className="field-label">Name in the report</span>
            <input
              className="input"
              placeholder="Researcher Pool"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
            />
          </div>
          <div className="field">
            <span className="field-label">Manager</span>
            <Select
              value={newUser}
              onChange={(v) => (v === NEW_MANAGER ? setAddingUser(true) : setNewUser(v))}
              options={[...options, { value: NEW_MANAGER, label: "+ New manager…" }]}
            />
          </div>
          <div className="field">
            <span className="field-label">From</span>
            <input
              className="input"
              type="month"
              value={String(newFrom).slice(0, 7)}
              onChange={(e) => setNewFrom(e.target.value ? `${e.target.value}-01` : "")}
              title="Leave blank for 'always'. Pick the month the handover takes effect."
            />
          </div>
          <button
            className="btn btn--primary"
            onClick={add}
            disabled={!newCode || !newUser || saving}
          >
            {saving ? "Saving…" : "Add mapping"}
          </button>
        </div>

        {addingUser && (
          <form className="dept-add" onSubmit={createManager} style={{ borderTop: "1px solid var(--border-soft)" }}>
            <div className="field">
              <span className="field-label">New manager login</span>
              <input
                className="input"
                placeholder="e.g. tm"
                value={mgrName}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                style={{ width: 130 }}
                onChange={(e) => setMgrName(e.target.value.toLowerCase())}
              />
            </div>
            <div className="field">
              <span className="field-label">Password</span>
              <input
                className="input"
                type="password"
                placeholder="8+ chars, one symbol"
                value={mgrPass}
                autoComplete="new-password"
                style={{ minWidth: 190 }}
                onChange={(e) => setMgrPass(e.target.value)}
              />
            </div>
            <button
              className="btn btn--primary"
              type="submit"
              disabled={saving || !mgrName || !mgrPass}
            >
              {saving ? "Creating…" : "Create manager"}
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => { setAddingUser(false); setMgrName(""); setMgrPass(""); }}
            >
              Cancel
            </button>
            <span className="toolbar-note" style={{ flexBasis: "100%" }}>
              2–10 lowercase letters, and a password of at least 8 characters
              including a symbol. The new login appears everywhere a manager can be
              picked; set its page access under <strong>Users</strong>.
            </span>
          </form>
        )}

        <p className="toolbar-note dept-hint">
          Leave <strong>From</strong> blank for a department that has always belonged
          to one manager. To record a handover, add a second row for the same code
          with the month it changed — the earlier row stays, and every row is
          attributed to whoever owned the office in its own month. Changing a
          mapping re-derives the existing rows immediately; no reimport needed. Need a manager that
          does not exist yet? Pick <strong>+ New manager…</strong> in the Manager list
          to create one without leaving this page.
        </p>

        <div className="tbl-wrap tbl-wrap--flush">
          <table className="tbl tbl-zebra">
            <thead>
              <tr>
                <th style={{ width: 90 }}>Code</th>
                <th className="col-flex">Name in the report</th>
                <th style={{ width: 130 }}>Manager</th>
                <th style={{ width: 120 }}>From</th>
                <th style={{ width: 130 }}>Last changed</th>
                <th style={{ width: 1 }} />
              </tr>
            </thead>
            <tbody>
              {mappings.map((m) => (
                <tr key={m.id ?? `${m.code}-${m.effective_from}`}>
                  <td>
                    <span className="code-tag">{m.code}</span>
                    {Number(m.versions) > 1 && (
                      <span className="badge" style={{ marginLeft: 6 }}>
                        {m.versions}
                      </span>
                    )}
                  </td>
                  <td className="truncate" title={m.label}>
                    {m.label || <span className="dash">—</span>}
                  </td>
                  <td>
                    <select
                      className="mgr-select"
                      value={m.username}
                      onChange={(e) => remap(m, e.target.value)}
                    >
                      {users.map((u) => (
                        <option key={u} value={u}>{u.toUpperCase()}</option>
                      ))}
                    </select>
                  </td>
                  <td className={m.effective_from === ALWAYS ? "muted" : ""}>
                    {m.effective_from === ALWAYS ? "always" : fmtDate(m.effective_from)}
                  </td>
                  <td className="muted">
                    {m.updated_at ? fmtDate(m.updated_at) : <span className="dash">—</span>}
                  </td>
                  <td>
                    <button className="btn btn--sm btn--danger" onClick={() => remove(m)}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
